import { SortAscendingIcon } from "@phosphor-icons/react/dist/csr/SortAscending";
import { SortDescendingIcon } from "@phosphor-icons/react/dist/csr/SortDescending";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@volca/ui/components/ui/popover";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import type { IAiSpreadsheetRegion } from "@/payroll/ai-script/types";
import { SpreadsheetColumnFilterEditor } from "./spreadsheet-column-filter-editor";
import {
  SPREADSHEET_REGION_MIXED_COLUMN_TYPE,
  hasActiveSpreadsheetColumnFilter,
  type SpreadsheetColumnFilter,
  type SpreadsheetRegionColumnType,
  type SpreadsheetRegionViewState,
} from "./spreadsheet-region-view";

export interface SpreadsheetColumnMenuTarget {
  x: number;
  y: number;
  column: number;
  region: IAiSpreadsheetRegion;
  values: string[];
  columnType: SpreadsheetRegionColumnType;
  /** The column's own header text, so the menu can name what it is editing. */
  label: string;
  focusOnOpen?: boolean;
}

interface SpreadsheetColumnMenuProps {
  target: SpreadsheetColumnMenuTarget | null;
  viewState: SpreadsheetRegionViewState | undefined;
  onClose: () => void;
  onFocusReturn: () => void;
  onChange: (state: SpreadsheetRegionViewState) => void;
}

interface SpreadsheetColumnMenuEditorProps
  extends Omit<SpreadsheetColumnMenuProps, "target" | "viewState"> {
  target: SpreadsheetColumnMenuTarget;
  state: SpreadsheetRegionViewState;
  committedFilter: SpreadsheetColumnFilter;
}

const EMPTY_COLUMN_FILTER: SpreadsheetColumnFilter = {
  selectedValues: null,
  condition: null,
  conditionValue: "",
};

const filtersAreEqual = (
  left: SpreadsheetColumnFilter,
  right: SpreadsheetColumnFilter,
) =>
  left.condition === right.condition &&
  left.conditionValue === right.conditionValue &&
  (left.selectedValues === right.selectedValues ||
    (left.selectedValues !== null &&
      right.selectedValues !== null &&
      left.selectedValues.length === right.selectedValues.length &&
      left.selectedValues.every(
        (value, index) => value === right.selectedValues?.[index],
      )));

const describeActiveState = (
  state: SpreadsheetRegionViewState,
  column: number,
): string | null => {
  const filter = state.filters[String(column)];
  const parts: string[] = [];

  if (
    !hasActiveSpreadsheetColumnFilter(filter) &&
    state.sort?.column !== column
  ) {
    return null;
  }

  if (filter?.selectedValues !== null && filter?.selectedValues !== undefined) {
    parts.push(`${filter.selectedValues.length} values kept`);
  }
  if (filter?.condition && filter.conditionValue !== "") {
    parts.push("condition applied");
  }
  if (state.sort?.column === column) {
    parts.push(state.sort.direction === "asc" ? "sorted A–Z" : "sorted Z–A");
  }

  return parts.length > 0 ? parts.join(" · ") : null;
};

const SpreadsheetColumnMenuEditor = ({
  target,
  state,
  committedFilter,
  onClose,
  onFocusReturn,
  onChange,
}: SpreadsheetColumnMenuEditorProps) => {
  const columnKey = String(target.column);
  const setting = target.region.column_settings[columnKey];
  const activeSummary = describeActiveState(state, target.column);

  /**
   * Building a filter is many small decisions — a dozen checkboxes, then a
   * condition — and re-projecting the sheet after each one is wasted work the
   * user waits through on a large tab. So the filter is edited as a local draft
   * and only committed on Apply — or by any other commit, such as sorting.
   */
  const [draft, setDraft] = useState<SpreadsheetColumnFilter>(committedFilter);

  const isDirty =
    !filtersAreEqual(draft, committedFilter) &&
    // An untouched column starts with everything selected; picking every value
    // by hand is the same view, so do not call that a pending change.
    !(
      draft.selectedValues !== null &&
      committedFilter.selectedValues === null &&
      draft.selectedValues.length === target.values.length &&
      draft.condition === committedFilter.condition &&
      draft.conditionValue === committedFilter.conditionValue
    );

  /**
   * Single commit path. Every change carries the pending filter draft with it, so
   * sorting with checkboxes half-chosen applies them rather than throwing them
   * away — the draft exists to batch the work, not to be quietly lost.
   */
  const commit = (changes: Partial<SpreadsheetRegionViewState>) => {
    const filters = { ...state.filters };
    if (hasActiveSpreadsheetColumnFilter(draft)) filters[columnKey] = draft;
    else delete filters[columnKey];
    onChange({ ...state, filters, ...changes });
  };

  const applyDraft = () => {
    commit({});
    onClose();
  };

  const isSortedAscending =
    state.sort?.column === target.column && state.sort.direction === "asc";
  const isSortedDescending =
    state.sort?.column === target.column && state.sort.direction === "desc";

  return (
    <Popover
      open
      onOpenChange={(open, details) => {
        if (!open && details.reason === "focus-out" && !target.focusOnOpen) {
          details.cancel();
          return;
        }
        if (!open) onClose();
      }}
    >
      <PopoverTrigger
        nativeButton={false}
        render={
          <span
            aria-hidden
            className="pointer-events-none fixed h-0 w-0"
            style={{ left: target.x, top: target.y }}
          />
        }
      />
      <PopoverContent
        align="start"
        initialFocus={target.focusOnOpen}
        finalFocus={() => {
          if (target.focusOnOpen) onFocusReturn();
          return false;
        }}
        /**
         * Padding is applied per section rather than on the popover, so the rules
         * between sections reach both edges instead of floating in a gutter.
         * `overflow-hidden` matters: the popover itself must never scroll, or the
         * value list's own scrollbar sits next to a second one for the whole menu.
         */
        className={cn(
          "max-h-(--available-height) w-78 gap-0 overflow-hidden p-0",
        )}
      >
        <Stack
          align="start"
          gap={0}
          className="w-full shrink-0 px-4 pt-3.5 pb-3"
        >
          {/* Name the column being configured. "Column options" over a grid of
              twenty-one look-alike headers tells you nothing about which one the
              menu belongs to. */}
          <Text
            variant="label"
            className="truncate"
            title={target.label || `Column ${target.column}`}
          >
            {target.label || `Column ${target.column}`}
          </Text>
          {/* Green: this line reports a control the user has actively applied,
              so it takes the interactive colour. It was `text-brand`, one of
              legacy preset aliases never wired to Volca's palette — a near-white
              #F5F5F5 that rendered at 1.09:1 on this popover, i.e. invisible. */}
          {activeSummary && (
            <Text variant="caption" tone="brand">
              {activeSummary}
            </Text>
          )}
          {target.columnType === SPREADSHEET_REGION_MIXED_COLUMN_TYPE && (
            <Text variant="caption" tone="muted" className="pt-1 leading-snug">
              Holds more than one kind of value, so it sorts and compares as
              text.
            </Text>
          )}
        </Stack>

        {setting?.sortable && (
          <div className="grid shrink-0 grid-cols-2 gap-2 px-4 pb-3">
            {/* The direction already applied reads as pressed, so the menu shows
                the current state instead of only offering to change it. */}
            <Button
              variant={isSortedAscending ? "secondary" : "outline"}
              size="xs"
              aria-pressed={isSortedAscending}
              onClick={() =>
                commit({ sort: { column: target.column, direction: "asc" } })
              }
            >
              <SortAscendingIcon data-icon="inline-start" />
              Sort A to Z
            </Button>
            <Button
              variant={isSortedDescending ? "secondary" : "outline"}
              size="xs"
              aria-pressed={isSortedDescending}
              onClick={() =>
                commit({ sort: { column: target.column, direction: "desc" } })
              }
            >
              <SortDescendingIcon data-icon="inline-start" />
              Sort Z to A
            </Button>
          </div>
        )}

        {setting?.filterable && (
          <SpreadsheetColumnFilterEditor
            values={target.values}
            filter={draft}
            onChange={setDraft}
          />
        )}

        <Stack
          direction="row"
          justify="between"
          align="center"
          gap={2}
          className="w-full shrink-0 border-t border-line px-4 py-3"
        >
          <Button
            variant="ghost"
            size="xs"
            className="h-auto p-0"
            onClick={() => {
              const filters = { ...state.filters };
              delete filters[columnKey];
              onChange({
                ...state,
                filters,
                sort: state.sort?.column === target.column ? null : state.sort,
              });
              onClose();
            }}
          >
            Clear column
          </Button>
          <Stack direction="row" align="center" gap={2}>
            {setting?.filterable ? (
              <>
                <Button variant="outline" size="xs" onClick={onClose}>
                  Cancel
                </Button>
                {/* Applying is the only path that re-projects the sheet, so on a
                    big tab the user pays that cost once instead of per checkbox. */}
                <Button size="xs" onClick={isDirty ? applyDraft : onClose}>
                  Apply
                </Button>
              </>
            ) : (
              <Button variant="outline" size="xs" onClick={onClose}>
                Done
              </Button>
            )}
          </Stack>
        </Stack>
      </PopoverContent>
    </Popover>
  );
};

export const SpreadsheetColumnMenu = ({
  target,
  viewState,
  onClose,
  onFocusReturn,
  onChange,
}: SpreadsheetColumnMenuProps) => {
  if (!target) return null;

  const state = viewState ?? { filters: {}, sort: null };
  const committedFilter =
    state.filters[String(target.column)] ?? EMPTY_COLUMN_FILTER;

  return (
    <SpreadsheetColumnMenuEditor
      // A new editor instance owns each committed filter. This resets the local
      // draft without an effect or a state update during render.
      key={`${target.region.id}:${target.column}:${JSON.stringify(committedFilter)}`}
      target={target}
      state={state}
      committedFilter={committedFilter}
      onClose={onClose}
      onFocusReturn={onFocusReturn}
      onChange={onChange}
    />
  );
};
